/**
 * Where the bottle is, this frame.
 *
 * Pure animation state: it takes render seconds and a few numbers read out of
 * the simulation, and returns a pose in the screen-relative coordinates
 * src/config/viewmodel.ts documents. It imports no three.js, holds no scene
 * objects and can be stepped a thousand times in a test without a canvas -
 * which is the point of having it separate from BeerViewmodel.
 *
 * It also, and this matters more, writes to nothing. Every value it reads out
 * of the player is read-only, and the pose it returns only ever reaches a
 * material. Frame rate cannot change a run here any more than camera sway can.
 */

import { VIEWMODEL } from '../config/viewmodel';
import { MOMENTUM, PLAYER } from '../config/gameplay';
import { angleDelta, clamp, lerp, smoothStep } from '../utils/math';

/** One frame of bottle, in the screen units src/config/viewmodel.ts defines. */
export interface BottlePose {
  x: number;
  y: number;
  /** Length of the bottle as a fraction of the frame's height. */
  height: number;
  /** Radians. Positive swings the base to the right, around the mouth. */
  roll: number;
  /** Radians. Negative tips the base away from the camera. */
  pitch: number;
  /** 0..1. Zero means there is nothing to draw at all. */
  opacity: number;
}

export interface SwingInput {
  /** Render seconds since the last call. */
  dt: number;
  /** False on the menus: the bottle fades rather than popping. */
  visible: boolean;
  /** Camera angles, radians. Used for the lag, never written back. */
  yaw: number;
  pitch: number;
  /** Horizontal ground speed, m/s. Scales the bob. */
  speed: number;
  /** The camera's own head bob this frame, already in screen units. */
  bobLateral: number;
  bobVertical: number;
  /** The user's cosmetic sway setting, 0..2. Zero holds the bottle still. */
  swayIntensity: number;
  /** The lurch the sip in flight is applying right now, in local space. */
  lurchX: number;
  lurchY: number;
}

const SIP = VIEWMODEL.sip;
/** Both curves are up by here, and both come down together afterwards. */
const SIP_PEAK = Math.max(SIP.raise, SIP.tipStart + SIP.tip);
const SIP_RELEASE = SIP_PEAK + SIP.hold;
const SIP_TOTAL = SIP_RELEASE + SIP.lower;

/** Shared tail: both curves hold at the top and then fall over `lower`. */
function release(t: number): number {
  if (t < SIP_RELEASE) return 1;
  if (t < SIP_TOTAL) return 1 - smoothStep((t - SIP_RELEASE) / SIP.lower);
  return 0;
}

/** How far through "get it in front of your face" the hand is. */
export function reachCurve(t: number): number {
  if (t <= 0) return 0;
  if (t < SIP.raise) return smoothStep(t / SIP.raise);
  return release(t);
}

/**
 * How far through "roll it back" the wrist is.
 *
 * Trails `reachCurve` without waiting for it. The overlap is what makes this
 * read as one movement: rolling only after the hand has stopped looks like two
 * animations played back to back, and rolling in lockstep with it looks like a
 * sticker being spun.
 */
export function tipCurve(t: number): number {
  if (t <= SIP.tipStart) return 0;
  if (t < SIP.tipStart + SIP.tip) return smoothStep((t - SIP.tipStart) / SIP.tip);
  return release(t);
}

export class BeerSwing {
  /** Seconds since the last sip started. Parked at the end of the animation. */
  private sipTime = SIP_TOTAL;
  /** Render seconds, for the breathing. */
  private clock = 0;
  private fade = 0;
  private swayX = 0;
  private swayY = 0;
  private lastYaw: number | null = null;
  private lastPitch = 0;

  /** E went into the bottle. Restarts the pull, cutting off any in flight. */
  sip(): void {
    this.sipTime = 0;
  }

  /** R. Everything about the previous attempt goes, including the fade. */
  reset(): void {
    this.sipTime = SIP_TOTAL;
    this.clock = 0;
    this.fade = 0;
    this.swayX = 0;
    this.swayY = 0;
    this.lastYaw = null;
    this.lastPitch = 0;
  }

  /** True while a pull is on screen. Only the debug overlay cares. */
  get drinking(): boolean {
    return this.sipTime < SIP_TOTAL;
  }

  update(input: SwingInput): BottlePose {
    const dt = Math.max(0, input.dt);
    this.clock += dt;
    this.sipTime = Math.min(SIP_TOTAL, this.sipTime + dt);

    const settle = 1 - Math.exp(-dt * VIEWMODEL.fadeRate);
    this.fade += ((input.visible ? 1 : 0) - this.fade) * settle;

    const reach = reachCurve(this.sipTime);
    const tip = tipCurve(this.sipTime);

    const rest = VIEWMODEL.rest;
    const drink = VIEWMODEL.drink;

    // The pull is a deliberate movement, so it overrides the idle motion rather
    // than being added to it. A bottle that bobs while it is at your lip looks
    // like it is being held by somebody else.
    const idle = (1 - reach) * clamp(input.swayIntensity, 0, 2);
    this.trackLook(input, dt, idle);

    const speedFactor = clamp(input.speed / PLAYER.maxSpeed, 0, MOMENTUM.max);
    const bobX = input.bobLateral * VIEWMODEL.bob.lateral * speedFactor * idle;
    // Subtracted, not added: when the head rises the hand is still on its way.
    const bobY = -input.bobVertical * VIEWMODEL.bob.vertical * speedFactor * idle;
    const breath = Math.sin(this.clock * VIEWMODEL.idle.frequency) * VIEWMODEL.idle.amplitude * idle;

    const glug =
      Math.sin(this.sipTime * VIEWMODEL.glug.frequency) * VIEWMODEL.glug.amplitude * tip;

    // The mouth follows the *roll*, not the hand, and takes an arc on the way.
    // Both are geometry rather than taste: the neck only belongs below the
    // bottom edge once the bottle is tipped past vertical, because until then
    // that is where the whole bottle would be. See VIEWMODEL.raiseArc.
    const mouthY = lerp(rest.y, drink.y, tip) + Math.sin(Math.PI * tip) * VIEWMODEL.raiseArc;

    return {
      x: lerp(rest.x, drink.x, reach) + this.swayX + bobX - input.lurchX * VIEWMODEL.lurch.offset,
      y: mouthY + this.swayY + bobY + breath,
      // A shove forwards leaves the arm behind, which reads as the bottle
      // getting further away rather than moving sideways.
      height: lerp(rest.height, drink.height, reach) * (1 - input.lurchY * VIEWMODEL.lurch.depth),
      roll:
        lerp(rest.roll, drink.roll, tip) +
        glug +
        this.swayX * VIEWMODEL.sway.roll -
        input.lurchX * VIEWMODEL.lurch.roll,
      pitch: drink.pitch * tip,
      opacity: this.fade,
    };
  }

  /**
   * Viewmodel lag, driven by turn *rate* rather than by the per-frame delta -
   * otherwise the same flick of the mouse throws the bottle twice as far at
   * 60 fps as it does at 120, and the hand would visibly get heavier the worse
   * your computer is.
   */
  private trackLook(input: SwingInput, dt: number, scale: number): void {
    let targetX = 0;
    let targetY = 0;

    if (this.lastYaw !== null && dt > 1e-5) {
      const max = VIEWMODEL.sway.max;
      // Turn left (yaw up) and the bottle is left behind on the right.
      const yawRate = angleDelta(this.lastYaw, input.yaw) / dt;
      const pitchRate = (input.pitch - this.lastPitch) / dt;
      targetX = clamp(yawRate * VIEWMODEL.sway.gain, -max, max) * scale;
      targetY = clamp(-pitchRate * VIEWMODEL.sway.gain, -max, max) * scale;
    }
    this.lastYaw = input.yaw;
    this.lastPitch = input.pitch;

    const settle = 1 - Math.exp(-dt * VIEWMODEL.sway.damping);
    this.swayX += (targetX - this.swayX) * settle;
    this.swayY += (targetY - this.swayY) * settle;
  }
}
